// In-memory state mirror + all mutations. Every mutating action here follows the
// same pattern: write to IndexedDB first (via lib/db.ts), then update the in-memory
// arrays, then trigger a re-render — so the UI is always a reflection of durable
// storage, not the other way around (build brief "known gaps" #1).

import * as db from './lib/db';
import { uid, todayLocal } from './lib/format';
import { toast } from './lib/toast';
import { mirrorExpense, mirrorPhoto, flushSyncQueue } from './lib/serverMirror';
import type { Trip, Category, CurrencyDef, Expense, Photo, Filters } from './types';

export type TabId = 'dashboard' | 'expenses' | 'map' | 'reports' | 'settings';
export type MapView = 'map' | 'gallery';

export interface AppState {
  activeTab: TabId;
  trip: Trip;
  categories: Category[];
  currencies: CurrencyDef[];
  lastCurrency: string;
  expenses: Expense[];
  photos: Photo[];
  filters: Filters;
  mapView: MapView;
  reportCurrencyMode: string; // 'approx' | currency code
  online: boolean;
  ready: boolean;
}

const EMPTY_TRIP: Trip = {
  id: 'trip',
  name: '',
  description: '',
  countries: [],
  baseCurrency: 'EUR',
  totalBudget: 0,
  startDate: todayLocal(),
  endDate: todayLocal(),
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

export const state: AppState = {
  activeTab: 'dashboard',
  trip: EMPTY_TRIP,
  categories: [],
  currencies: [],
  lastCurrency: 'EUR',
  expenses: [],
  photos: [],
  filters: { category: 'all', currency: 'all', search: '' },
  mapView: 'map',
  reportCurrencyMode: 'approx',
  online: navigator.onLine,
  ready: false,
};

let rerender: () => void = () => {};
export function setRerenderer(fn: () => void): void {
  rerender = fn;
}
function render(): void {
  rerender();
}

export function categoryById(id: string): Category | undefined {
  return state.categories.find((c) => c.id === id);
}
export function categoryName(id: string): string {
  return categoryById(id)?.name || 'Other';
}
export function categoryIconKey(id: string): string {
  return categoryById(id)?.icon || 'sparkle';
}

/* ============================= HYDRATE ============================= */
export async function hydrate(): Promise<void> {
  const [trip, categories, currencies, expenses, photos, lastCurrency, mapView, reportMode] =
    await Promise.all([
      db.getTrip(),
      db.getAllCategories(),
      db.getAllCurrencies(),
      db.getAllExpenses(),
      db.getAllPhotos(),
      db.getMeta<string>('lastCurrency'),
      db.getMeta<MapView>('mapView'),
      db.getMeta<string>('reportCurrencyMode'),
    ]);
  state.trip = trip || EMPTY_TRIP;
  state.categories = categories;
  state.currencies = currencies;
  state.expenses = expenses;
  state.photos = photos;
  state.lastCurrency = lastCurrency || state.trip.baseCurrency || 'EUR';
  state.mapView = mapView || 'map';
  state.reportCurrencyMode = reportMode || 'approx';
  state.ready = true;
  window.addEventListener('online', () => {
    state.online = true;
    render();
    flushSyncQueue();
  });
  window.addEventListener('offline', () => {
    state.online = false;
    render();
  });
  // Catch up any expenses/photos that failed to reach the server-mirror endpoint
  // last session (e.g. app was closed while offline) — see lib/serverMirror.ts.
  flushSyncQueue();
}

export function setTab(id: TabId): void {
  state.activeTab = id;
  window.scrollTo(0, 0);
  render();
}

/* ============================= EXPENSES ============================= */
export interface ExpenseInput {
  date: string;
  amount: number;
  currency: string;
  categoryId: string;
  explanation: string;
}

export async function addExpense(input: ExpenseInput): Promise<void> {
  const now = new Date().toISOString();
  const expense: Expense = { id: uid(), ...input, createdAt: now, updatedAt: now };
  await db.putExpense(expense);
  state.expenses.push(expense);
  if (input.currency !== state.lastCurrency) {
    state.lastCurrency = input.currency;
    await db.setMeta('lastCurrency', input.currency);
  }
  toast('Expense saved');
  render();
  // Best-effort server-side mirror (CSV under docroot/data) — queued for retry if
  // offline/unreachable. IndexedDB above remains the actual source of truth.
  mirrorExpense({
    id: expense.id,
    date: expense.date,
    amount: expense.amount,
    currency: expense.currency,
    categoryId: expense.categoryId,
    categoryName: categoryName(expense.categoryId),
    explanation: expense.explanation,
    createdAt: expense.createdAt,
  });
}

export async function updateExpense(id: string, input: ExpenseInput): Promise<void> {
  const existing = state.expenses.find((e) => e.id === id);
  if (!existing) return;
  const updated: Expense = { ...existing, ...input, updatedAt: new Date().toISOString() };
  await db.putExpense(updated);
  Object.assign(existing, updated);
  toast('Expense updated');
  render();
}

export async function deleteExpenseById(id: string): Promise<void> {
  await db.deleteExpense(id);
  state.expenses = state.expenses.filter((e) => e.id !== id);
  toast('Expense deleted');
  render();
}

/* ============================= FILTERS / VIEW STATE ============================= */
export function setFilter<K extends keyof Filters>(key: K, value: Filters[K]): void {
  state.filters[key] = value;
  render();
}

export async function setMapView(v: MapView): Promise<void> {
  state.mapView = v;
  await db.setMeta('mapView', v);
  render();
}

export async function setReportMode(mode: string): Promise<void> {
  state.reportCurrencyMode = mode;
  await db.setMeta('reportCurrencyMode', mode);
  render();
}

/* ============================= PHOTOS ============================= */
export async function addPhoto(
  meta: Omit<Photo, 'id' | 'createdAt' | 'hasOriginal'>,
  blobs: { original: Blob; thumbnail: Blob }
): Promise<Photo> {
  const photo: Photo = { id: uid(), createdAt: new Date().toISOString(), hasOriginal: true, ...meta };
  await db.putPhoto(photo, blobs);
  state.photos.unshift(photo);
  render();
  // Best-effort server-side mirror (original file under docroot/data/photo + a CSV
  // metadata row) — queued for retry if offline/unreachable, same as mirrorExpense.
  mirrorPhoto(
    {
      id: photo.id,
      capturedAt: photo.capturedAt,
      lat: photo.lat,
      lng: photo.lng,
      place: photo.place,
      createdAt: photo.createdAt,
    },
    blobs.original
  );
  return photo;
}

export async function deletePhotoById(id: string): Promise<void> {
  await db.deletePhoto(id);
  state.photos = state.photos.filter((p) => p.id !== id);
  toast('Photo deleted');
  render();
}

/* ============================= TRIP DETAILS ============================= */
export async function updateTripField<K extends keyof Trip>(key: K, value: Trip[K]): Promise<void> {
  state.trip = { ...state.trip, [key]: value, updatedAt: new Date().toISOString() };
  await db.putTrip(state.trip);
  toast('Trip updated');
}

export async function addCountry(name: string): Promise<void> {
  const v = name.trim();
  if (!v) return;
  await updateTripField('countries', [...state.trip.countries, v]);
  render();
}
export async function removeCountry(index: number): Promise<void> {
  const next = state.trip.countries.filter((_, i) => i !== index);
  await updateTripField('countries', next);
  render();
}

/* ============================= CATEGORIES ============================= */
export async function addCategory(name: string, icon = 'sparkle'): Promise<void> {
  const v = name.trim();
  if (!v || state.categories.some((c) => c.name.toLowerCase() === v.toLowerCase())) return;
  const category: Category = { id: uid(), name: v, icon, sortOrder: state.categories.length };
  await db.putCategory(category);
  state.categories.push(category);
  render();
}

/** Returns true if the category can be deleted immediately; false if it's in use and
 * the caller should present the reassignment flow instead (spec §9.2). */
export function categoryInUse(id: string): boolean {
  return state.expenses.some((e) => e.categoryId === id);
}

export async function removeCategory(id: string): Promise<void> {
  await db.deleteCategory(id);
  state.categories = state.categories.filter((c) => c.id !== id);
  render();
}

export async function reassignAndDeleteCategory(fromId: string, toId: string): Promise<void> {
  const affected = state.expenses.filter((e) => e.categoryId === fromId);
  for (const e of affected) {
    e.categoryId = toId;
    e.updatedAt = new Date().toISOString();
    await db.putExpense(e);
  }
  await removeCategory(fromId);
  toast('Category removed');
}

/* ============================= CURRENCIES ============================= */
export async function addCurrency(codeInput: string): Promise<boolean> {
  const code = codeInput.trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(code) || state.currencies.some((c) => c.code === code)) return false;
  const currency: CurrencyDef = { code, symbol: code, isDefault: false };
  await db.putCurrency(currency);
  state.currencies.push(currency);
  render();
  return true;
}

export function currencyInUse(code: string): boolean {
  return state.expenses.some((e) => e.currency === code);
}

export async function removeCurrency(code: string): Promise<void> {
  if (currencyInUse(code)) {
    toast(`${code} is used by expenses — can't remove`);
    return;
  }
  await db.deleteCurrency(code);
  state.currencies = state.currencies.filter((c) => c.code !== code);
  render();
}

export async function setBaseCurrency(code: string): Promise<void> {
  await updateTripField('baseCurrency', code);
  for (const c of state.currencies) {
    const wasDefault = c.isDefault;
    c.isDefault = c.code === code;
    if (wasDefault !== c.isDefault) await db.putCurrency(c);
  }
  toast('Base currency set to ' + code);
  render();
}

/* ============================= RESET ============================= */
export async function resetAllData(): Promise<void> {
  const database = await db.getDB();
  await Promise.all(
    ['trip', 'categories', 'currencies', 'expenses', 'photos', 'photoBlobs', 'meta'].map((s) =>
      database.clear(s as never)
    )
  );
  await hydrate();
  render();
}
